import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    const { grid } = this.refs;
    if (grid) this.#updateGridImageSizes(grid.getAttribute('product-grid-view') || 'default');
    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
    this.#updateGridImageSizes(value);
  }

  /**
   * Updates the responsive image `sizes` hint to match the current grid view.
   *
   * @param {string} value
   */
  #updateGridImageSizes(value) {
    const { grid } = this.refs;
    if (!grid) return;

    let cardWidth = '100vw';
    if (mediaQueryLarge.matches) {
      /** @type {Record<string, string>} */
      const desktopWidths = {
        'columns-2': '50vw',
        'columns-4': '25vw',
        'columns-6': '16.667vw',
      };
      cardWidth = desktopWidths[value] || '25vw';
    } else if (value === 'default') {
      cardWidth = '50vw';
    }

    for (const image of grid.querySelectorAll('.product-media__image')) {
      if (image instanceof HTMLImageElement) {
        image.sizes = `auto, ${cardWidth}`;
      }
    }
  }

  /**
   * Handles the media query change event.
   *
   * Restores the layout the visitor previously chose for the viewport they
   * just switched to, falling back to that viewport's default option.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const isDesktop = event.matches;
    const viewport = isDesktop ? 'desktop' : 'mobile';
    const optionName = isDesktop ? 'grid' : 'grid-mobile';
    const options = this.querySelectorAll(`input[type="radio"][name="${optionName}"]`);

    // Only honor a stored view when its controls exist for this viewport.
    let storedLayout = options.length > 0 ? sessionStorage.getItem(`product-grid-view-${viewport}`) : null;
    if (isDesktop && storedLayout) {
      if (storedLayout === 'default') storedLayout = 'columns-4';
      if (storedLayout === 'zoom-out') storedLayout = 'columns-6';
    }

    let targetElement = null;
    if (storedLayout) {
      targetElement = this.querySelector(
        `input[type="radio"][name="${optionName}"][value="${storedLayout}"]`
      );
    }

    if (!(targetElement instanceof HTMLInputElement)) {
      targetElement = isDesktop
        ? this.querySelector('[data-grid-layout="desktop-default-option"]')
        : this.querySelector('[data-grid-layout="mobile-option"]');
    }

    if (targetElement instanceof HTMLInputElement) {
      targetElement.checked = true;
      this.#setLayout(targetElement.value);
      return;
    }

    // No control matches this viewport (e.g. organic has no desktop picker). Fall back to
    // the layout's intrinsic default so the grid is never stranded on a view that doesn't
    // apply — leaving organic on a column/mobile value would collapse it to a single column.
    const { grid } = this.refs;
    const isOrganic = grid?.classList.contains('product-grid--organic');
    this.#setLayout(isDesktop ? (isOrganic ? 'default' : 'columns-4') : 'default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
